/**
 * Shared YQ Storefront client for any theme module.
 *
 * Theme settings arrive on window.YQ_STOREFRONT. This file signs and sends requests.
 * Callers pass a path relative to the API base, such as "shopify/reviews" or "shopify/ping".
 * Do not include "/api" in that path, and never send storefrontKey in a header.
 *
 * window.YqStorefrontApi
 *   isReady()
 *   whenReady(timeoutMs = 2000) -> Promise<context>
 *   getContext() -> { enabled, apiBase, shopCode, productId, productHandle, productTitle, shopDomain, shopUrl }
 *   fetch(path, { method, query, body, headers, idempotencyKey }) -> Promise<data>
 *   ping()
 *
 * window.yqStorefrontFetch(path, options) is the same fetch, for short call sites.
 */
(function (global) {
  'use strict';

  const textEncoder = new TextEncoder();

  function bytesToHex(bytes) {
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  function sha256Hex(raw) {
    return crypto.subtle.digest('SHA-256', textEncoder.encode(raw == null ? '' : String(raw))).then((buffer) => {
      return bytesToHex(new Uint8Array(buffer));
    });
  }

  function hmacSha256Hex(secret, message) {
    return crypto.subtle
      .importKey('raw', textEncoder.encode(String(secret)), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
      .then((key) => crypto.subtle.sign('HMAC', key, textEncoder.encode(String(message))))
      .then((signature) => bytesToHex(new Uint8Array(signature)));
  }

  function getConfig() {
    return global.YQ_STOREFRONT || null;
  }

  function cleanPath(path) {
    return String(path || '')
      .replace(/^\/+/, '')
      .replace(/^api\//, '');
  }

  function isReady() {
    const cfg = getConfig();
    return !!(cfg && cfg.enabled === true && cfg.apiBase && cfg.shopCode && cfg.storefrontKey);
  }

  function getContext() {
    const cfg = getConfig() || {};
    return {
      enabled: cfg.enabled === true,
      apiBase: cfg.apiBase || '',
      shopCode: cfg.shopCode || '',
      productId: cfg.productId == null ? '' : String(cfg.productId),
      productHandle: cfg.productHandle || '',
      productTitle: cfg.productTitle || '',
      shopDomain: cfg.shopDomain || '',
      shopUrl: cfg.shopUrl || ''
    };
  }

  function createStorefrontError(message, status, payload) {
    const error = new Error(message || 'Storefront request failed');
    error.name = 'YqStorefrontError';
    error.status = status;
    error.payload = payload;
    return error;
  }

  function assertEnabled(cfg) {
    if (!cfg || cfg.enabled !== true) {
      throw createStorefrontError('Storefront API disabled', 0, null);
    }
    if (!cfg.apiBase || !cfg.shopCode || !cfg.storefrontKey) {
      throw createStorefrontError('Storefront API config incomplete', 0, null);
    }
  }

  function buildUrl(apiBase, path, query) {
    const base = String(apiBase).replace(/\/+$/, '');
    const url = new URL(base + '/' + cleanPath(path));

    if (query && typeof query === 'object') {
      Object.entries(query).forEach(([key, value]) => {
        if (value != null && value !== '') url.searchParams.set(key, String(value));
      });
    }

    return url;
  }

  function whenReady(timeoutMs) {
    const limit = Number(timeoutMs) > 0 ? Number(timeoutMs) : 2000;
    if (isReady()) return Promise.resolve(getContext());

    return new Promise((resolve, reject) => {
      const started = Date.now();
      const timer = global.setInterval(() => {
        if (isReady()) {
          global.clearInterval(timer);
          resolve(getContext());
          return;
        }
        if (Date.now() - started >= limit) {
          global.clearInterval(timer);
          reject(createStorefrontError('Storefront API disabled', 0, null));
        }
      }, 50);
    });
  }

  /**
   * @param {string} path Path relative to apiBase, e.g. "shopify/reviews"
   * @param {{ method?: string, query?: object, body?: object|null, headers?: object, idempotencyKey?: string }} [options]
   * @returns {Promise<*>} response.data when code === 0
   */
  async function yqStorefrontFetch(path, options = {}) {
    const cfg = getConfig();
    assertEnabled(cfg);

    const method = String(options.method || 'GET').toUpperCase();
    const url = buildUrl(cfg.apiBase, path, options.query);
    const hasBody = options.body != null && method !== 'GET' && method !== 'HEAD';
    const rawBody = hasBody ? JSON.stringify(options.body) : '';
    const timestamp = String(Math.floor(Date.now() / 1000));
    const bodyHash = await sha256Hex(rawBody);
    const canonical = [cfg.shopCode, timestamp, method, '/api/' + cleanPath(path), bodyHash].join('\n');
    const sign = await hmacSha256Hex(cfg.storefrontKey, canonical);
    const headers = Object.assign({ Accept: 'application/json' }, options.headers || {});

    headers['X-YQ-Shop-Code'] = cfg.shopCode;
    headers['X-YQ-Timestamp'] = timestamp;
    headers['X-YQ-Sign'] = sign;

    if (hasBody) headers['Content-Type'] = 'application/json';
    if (options.idempotencyKey) headers['Idempotency-Key'] = String(options.idempotencyKey);

    const response = await fetch(url.toString(), {
      method,
      headers,
      body: hasBody ? rawBody : undefined,
      credentials: 'omit'
    });

    let json = null;
    try {
      json = await response.json();
    } catch (parseError) {
      throw createStorefrontError('Storefront response is not JSON', response.status, null);
    }

    if (!response.ok || !json || json.code !== 0) {
      throw createStorefrontError((json && json.message) || 'Storefront request failed', response.status, json);
    }

    return json.data;
  }

  function yqStorefrontPing() {
    return yqStorefrontFetch('shopify/ping');
  }

  global.yqStorefrontFetch = yqStorefrontFetch;
  global.YqStorefrontApi = Object.freeze({
    isReady,
    whenReady,
    getContext,
    fetch: yqStorefrontFetch,
    ping: yqStorefrontPing
  });
})(typeof window !== 'undefined' ? window : this);
